import { useCallback, useEffect, useState } from 'react';
import { subscribeToTable } from '@/lib/supabase-client';
import { useAuth } from '@/providers/auth-provider';
import { notificationService } from '@socialflows/shared-lib';
import type { AppNotification } from '@socialflows/shared-lib';
import { useNotificationSound } from './use-notification-sound';

export interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (ids: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotifications(): NotificationState {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { playSound } = useNotificationSound();

  const fetchNotifications = useCallback(async () => {
    if (!profile?.accountId) return;
    try {
      setLoading(true);
      const result = await notificationService.getNotifications({ limit: 50 });
      setNotifications(result.data);
      setUnreadCount(result.unreadCount);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [profile?.accountId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!profile?.accountId) return;

    const channel = subscribeToTable(
      'notifications',
      (payload: any) => {
        if (payload.eventType !== 'INSERT') return;

        const row = payload.new;
        if (!row) return;

        const notification: AppNotification = notificationService.mapToEntity(row);

        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);

        playSound({ type: notification.type, priority: notification.priority });
      },
      { column: 'account_id', value: profile.accountId }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [profile?.accountId, playSound]);

  const markAsRead = useCallback(async (ids: string[]) => {
    await notificationService.markAsRead(ids);
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, isRead: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - ids.length));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications,
  };
}
